import { ArrowRight, Sparkles, ArrowDown } from "lucide-react";

const Hero = () => {
    const partners = [
        "K-12 Districts",
        "Municipalities",
        "Universities",
        "Enterprises",
        "Non-Profits",
        "Hospitality Groups",
        "Construction Firms",
    ];

    return (
        <section
            id="home"
            className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden"
        >
            {/* Background layers */}
            <div className="absolute inset-0 hero-glow"></div>
            <div
                className="absolute inset-0 opacity-[0.15]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(46,144,250,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(46,144,250,0.08) 1px, transparent 1px)",
                    backgroundSize: "60px 60px",
                    maskImage:
                        "radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent 70%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent 70%)",
                }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
                {/* Eyebrow */}
                <div
                    className="flex items-center gap-2 mb-8 opacity-0 animate-fade-up"
                    style={{ animationDelay: "0.1s" }}
                >
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-brand opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-brand"></span>
                        </span>
                        <span className="text-[11px] font-mono uppercase tracking-widest text-white/70">
                            APS Data Technologies — Est. 2011
                        </span>
                    </div>
                </div>

                {/* Headline */}
                <h1
                    className="text-[13vw] sm:text-[10vw] md:text-[8vw] lg:text-[7rem] leading-[0.95] tracking-[-0.03em] mb-6 opacity-0 animate-fade-up"
                    style={{ animationDelay: "0.2s" }}
                    data-testid="hero-headline"
                >
                    <span className="block font-display text-white">
                        We build the <em className="italic text-accent-brand">systems</em>
                    </span>
                    <span className="block font-display text-white">
                        that run modern <em className="italic">institutions.</em>
                    </span>
                </h1>

                {/* Sub */}
                <div
                    className="max-w-2xl mb-12 opacity-0 animate-fade-up"
                    style={{ animationDelay: "0.35s" }}
                >
                    <p className="text-white/60 text-lg md:text-xl leading-relaxed">
                        Founded in 2011. Since 2019, we&apos;ve been engineering
                        AI/ML applications across government, higher-ed, K-12,
                        enterprise, hospitality, and construction — turning
                        complex operations into clean, intelligent software.
                    </p>
                </div>

                {/* CTAs */}
                <div
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-20 opacity-0 animate-fade-up"
                    style={{ animationDelay: "0.5s" }}
                >
                    <button
                        onClick={() =>
                            document
                                .getElementById("contact")
                                ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-accent-brand text-ink-950 font-semibold hover:bg-white transition-all duration-300 shadow-[0_0_30px_rgba(46,144,250,0.3)]"
                        data-testid="hero-get-started-button"
                    >
                        Start a project
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                        onClick={() =>
                            document
                                .getElementById("work")
                                ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-white/15 text-white hover:bg-white/5 transition-all duration-300"
                        data-testid="hero-explore-solutions-button"
                    >
                        <Sparkles className="w-4 h-4 text-accent-brand" />
                        See our work
                    </button>
                </div>

                {/* Metric strip */}
                <div
                    className="grid grid-cols-3 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10 opacity-0 animate-fade-up"
                    style={{ animationDelay: "0.65s" }}
                    data-testid="hero-metrics"
                >
                    {[
                        { value: "14+", label: "Years shipping" },
                        { value: "2019", label: "AI/ML focus since" },
                        { value: "7", label: "Industry verticals" },
                    ].map((m, i) => (
                        <div
                            key={i}
                            className="bg-ink-950 p-6 md:p-8"
                            data-testid={`hero-metric-${i}`}
                        >
                            <div className="text-3xl md:text-4xl lg:text-5xl font-display text-white tracking-tight">
                                {m.value}
                            </div>
                            <div className="text-[11px] font-mono uppercase tracking-widest text-white/40 mt-2">
                                {m.label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Ticker */}
                <div className="mt-16 marquee-mask overflow-hidden">
                    <div className="flex gap-12 animate-marquee whitespace-nowrap w-max">
                        {[...partners, ...partners, ...partners].map((p, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-3 text-white/30 text-sm font-mono uppercase tracking-widest"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-accent-brand"></span>
                                {p}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Scroll cue */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/30">
                <span className="text-[10px] font-mono uppercase tracking-widest">
                    Scroll
                </span>
                <ArrowDown className="w-4 h-4 animate-bounce" />
            </div>
        </section>
    );
};

export default Hero;
